"use client";

import {
	type GroundingSearchListItem,
	getPlatformDisplayName,
	getPlatformLogo,
} from "@repo/core/client";
import { cn } from "@ui/lib";
import { ChevronDown, ChevronUp } from "lucide-react";
import Image from "next/image";
import { useState } from "react";

/**
 * Grounding searches WITHOUT ranks: the query, the platforms that ran it and
 * how many report prompts used it.
 *
 * For reports stored as `GroundingSearchListV2` (rank tracking paused), where
 * no Google or Brave checks ran. Legacy reports keep `GroundingSearchHeatMap` /
 * `GroundingSearchTable`, which are built around rank cells, gap filters and
 * impact scores that this shape does not have.
 *
 * Styled on the same fixed light palette as `GroundingSearchHeatMap` (gray
 * borders, `bg-gray-50` header), because the report body it renders in is
 * light-only; theme tokens would turn it into a dark card inside a white report.
 */
interface GroundingSearchListProps {
	items: GroundingSearchListItem[];
	/** Rows shown before the overflow control. Omit to show every row. */
	maxItems?: number;
	/**
	 * What happens past `maxItems`: "toggle" adds a show-all button, "count"
	 * prints "and N more searches" (for summaries that point to the full list).
	 */
	overflow?: "toggle" | "count";
	className?: string;
}

function PlatformLabels({ platforms }: { platforms: string[] }) {
	// Name the assistant that ran the search ("Google Gemini", not the shared
	// "Google" index label the rank views use); show each label once.
	const labels = new Map<string, string | null>();
	for (const platform of platforms) {
		const name = getPlatformDisplayName(platform);
		if (!labels.has(name)) {
			labels.set(name, getPlatformLogo(platform));
		}
	}

	return (
		<span className="flex flex-wrap items-center gap-x-2 gap-y-1">
			{Array.from(labels.entries()).map(([name, logo]) => (
				<span
					key={name}
					className="inline-flex items-center gap-1 text-xs text-gray-600"
				>
					{logo && (
						<Image
							src={logo}
							alt=""
							width={14}
							height={14}
							className="rounded-sm flex-shrink-0"
						/>
					)}
					{name}
				</span>
			))}
		</span>
	);
}

export function GroundingSearchList({
	items,
	maxItems,
	overflow = "toggle",
	className,
}: GroundingSearchListProps) {
	const [showAll, setShowAll] = useState(false);

	if (items.length === 0) return null;

	const limited = maxItems !== undefined && items.length > maxItems;
	const visible = limited && !showAll ? items.slice(0, maxItems) : items;
	const hiddenCount = items.length - visible.length;

	return (
		<div className={cn("space-y-3", className)}>
			<div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
				<div className="hidden grid-cols-[minmax(0,1fr)_160px_96px] border-b border-gray-200 bg-gray-50 text-xs font-medium text-gray-600 sm:grid">
					<div className="px-3 py-2">Search</div>
					<div className="border-l border-gray-200 px-3 py-2">
						Run by
					</div>
					<div
						className="border-l border-gray-200 px-3 py-2 text-right"
						title="How many of this report's prompts led an AI platform to run this search"
					>
						Times used
					</div>
				</div>
				<ul className="divide-y divide-gray-200">
					{visible.map((item) => (
						<li
							key={item.groundingSearchId}
							className="grid grid-cols-1 gap-1 px-3 py-2 transition-colors hover:bg-gray-50 sm:grid-cols-[minmax(0,1fr)_160px_96px] sm:gap-0 sm:px-0"
						>
							<span className="break-words text-sm text-gray-900 sm:px-3">
								{item.query}
							</span>
							<span className="sm:border-l sm:border-gray-200 sm:px-3">
								<PlatformLabels platforms={item.platforms} />
							</span>
							<span className="text-xs text-gray-600 sm:border-l sm:border-gray-200 sm:px-3 sm:text-right sm:text-sm">
								<span className="sm:hidden">Times used: </span>
								{item.usageCount}
							</span>
						</li>
					))}
				</ul>
			</div>

			{limited && overflow === "count" && hiddenCount > 0 && (
				<p className="text-sm text-gray-600">
					and {hiddenCount} more search{hiddenCount === 1 ? "" : "es"}
				</p>
			)}

			{limited && overflow === "toggle" && (
				<button
					type="button"
					onClick={() => setShowAll(!showAll)}
					className="mx-auto flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800"
				>
					{showAll ? (
						<>
							<ChevronUp className="h-4 w-4" />
							Show less
						</>
					) : (
						<>
							<ChevronDown className="h-4 w-4" />
							Show all {items.length} searches
						</>
					)}
				</button>
			)}
		</div>
	);
}
